import React from 'react';

import { CheckIcon } from '@modernloop/shared/icons';
import { CircularProgress, ListItemIcon, MenuItem, Typography } from '@mui/material';

import { useOrganizationMemberships } from './useOrganizationMemberships';
import { useSwitchActiveOrganization } from './useSwitchActiveOrganization';

type Props = {
  /** Called when the user picks an Organization, so the host menu can close itself. */
  onSelect?: () => void;
};

/**
 * The Organization rows of the switcher: one `MenuItem` per Membership, the active one checked.
 *
 * Renders nothing for a user with one Membership, which is every user until they are invited into a
 * second Organization. The caller keeps showing its own Organization name in that case.
 *
 * Made of bare menu items rather than a self-contained popover, so each host puts the rows in its
 * own `Menu`. Three hosts exist:
 *
 * 1. `AccountMenuSwitchTenantItem` opens the rows in a submenu of the account menu.
 * 2. `OrganizationSwitcherButton` opens the rows in a menu on the onboarding shell.
 * 3. `OrgSetupAccountMenuButton` opens the rows above Log out on the Organization setup page.
 *
 * The rows have no title row. Each host control already names the action or the active
 * Organization.
 */
const OrganizationSwitcherItems = ({ onSelect }: Props): JSX.Element | null => {
  const memberships = useOrganizationMemberships();
  const { switchingOrganizationId, switchToOrganization } = useSwitchActiveOrganization();

  if (memberships.length < 2) {
    return null;
  }

  return (
    <>
      {memberships.map((membership) => {
        const organizationId = membership.organization.id;
        const isSwitching = switchingOrganizationId === organizationId;

        return (
          <MenuItem
            key={organizationId}
            data-testid={`organization-switcher-option-${organizationId}`}
            selected={membership.isActive}
            disabled={switchingOrganizationId !== null}
            onClick={async () => {
              // The menu stays open while the switch runs, so the row keeps its spinner. On
              // success the page reloads and the menu goes with it.
              if (!membership.isActive) {
                await switchToOrganization(organizationId);
              }
              onSelect?.();
            }}
          >
            <ListItemIcon>
              {isSwitching ? <CircularProgress size={16} /> : null}
              {!isSwitching && membership.isActive ? <CheckIcon /> : null}
            </ListItemIcon>
            {/* The user asked for the Organization name only, with no role caption. */}
            <Typography color="text.primary" noWrap>
              {membership.organization.name}
            </Typography>
          </MenuItem>
        );
      })}
    </>
  );
};

export default OrganizationSwitcherItems;
